<template>
  <div class="jsr">
    <draggable v-model="localSteps" handle=".jsr-step__drag" @end="emit">
      <div v-for="(step, stepIndex) in localSteps" :key="step.id || stepIndex" class="jsr-step">
        <div class="jsr-step__head" :class="{'is-active': isActive('step', step.id)}"
             @click="select('step', step.id, stepIndex)">
          <span class="jsr-step__drag"><feather-icon icon="MenuIcon" size="13"/></span>
          <span class="jsr-step__index">{{ stepIndex + 1 }}</span>
          <span class="jsr-step__title">{{ step.title || msg('step') + ' ' + (stepIndex + 1) }}</span>
          <feather-icon v-if="step.condition" icon="ZapIcon" size="12" class="jsr-step__cond"
                        v-b-tooltip.hover :title="msg('showonlyif')"/>
          <feather-icon v-if="isProtectedStep(step)" icon="LockIcon" size="12" class="jsr-step__lock"
                        v-b-tooltip.hover :title="msg('detailsstepprotected')"/>
          <b-button v-else variant="flat-danger" size="sm" class="btn-icon jsr-step__del"
                    @click.stop="removeStep(stepIndex)">
            <feather-icon icon="Trash2Icon" size="12"/>
          </b-button>
        </div>

        <draggable :value="step.questions || []" handle=".jsr-q__drag"
                   @input="setQuestions(stepIndex, $event)">
          <div v-for="question in (step.questions || [])" :key="question.id"
               class="jsr-q" :class="{'is-active': isActive('question', question.id)}"
               @click="select('question', question.id, stepIndex)">
            <span class="jsr-q__drag"><feather-icon icon="MenuIcon" size="12"/></span>
            <feather-icon :icon="iconOf(question)" size="13" class="jsr-q__icon"/>
            <span class="jsr-q__label">{{ question.label || question.key || msg('question') }}</span>
            <feather-icon v-if="question.condition" icon="ZapIcon" size="11" class="jsr-q__cond"/>
            <span v-if="hasProblem(question)" class="jsr-q__dot"
                  v-b-tooltip.hover :title="problemText(question)"></span>
            <span class="jsr-q__actions">
              <b-button variant="flat-secondary" size="sm" class="btn-icon jsr-q__btn"
                        v-b-tooltip.hover :title="msg('duplicate')"
                        @click.stop="duplicateQuestion(stepIndex, question)">
                <feather-icon icon="CopyIcon" size="11"/>
              </b-button>
              <feather-icon v-if="isProtectedQuestion(question)" icon="LockIcon" size="11" class="jsr-q__lock"
                            v-b-tooltip.hover :title="msg('detailsstepprotected')"/>
              <b-button v-else variant="flat-danger" size="sm" class="btn-icon jsr-q__btn"
                        v-b-tooltip.hover :title="msg('Delete')"
                        @click.stop="removeQuestion(stepIndex, question)">
                <feather-icon icon="Trash2Icon" size="11"/>
              </b-button>
            </span>
          </div>
        </draggable>

        <button type="button" class="jsr-add" @click="addQuestion(stepIndex)">
          <feather-icon icon="PlusIcon" size="12"/>{{ msg('addquestion') }}
        </button>
      </div>
    </draggable>

    <b-button variant="outline-primary" size="sm" block class="mt-1" @click="addStep">
      <feather-icon icon="PlusIcon" size="14" class="mr-25"/>{{ msg('addstep') }}
    </b-button>
  </div>
</template>

<script>
import {mapGetters} from 'vuex'
import draggable from 'vuedraggable'
import {questionType} from '@/model/journey/journeyTypes'

/** Left rail of the builder: drag-ordered steps and their questions. */
export default {
  name: 'journeyStepRail',
  components: {draggable},
  props: {
    value: {type: Array, default: () => []},
    selected: {type: Object, default: null},
    duplicateKeys: {type: Array, default: () => []},
  },
  data() {
    return {localSteps: []}
  },
  computed: {
    ...mapGetters('translator', ['msg']),
  },
  created() {
    this.localSteps = [...(this.value || [])];
  },
  watch: {
    value(list) {
      this.localSteps = [...(list || [])];
    },
  },
  methods: {
    isActive(type, id) {
      return this.selected && this.selected.type === type && this.selected.id === id;
    },
    select(type, id, stepIndex) {
      this.$emit('select', {type, id, stepIndex});
    },
    iconOf(question) {
      const type = questionType(question.type);
      return type ? type.icon : 'HelpCircleIcon';
    },
    hasProblem(question) {
      return !question.key || !question.label || this.duplicateKeys.includes(question.key);
    },
    problemText(question) {
      if (!question.key) return this.msg('questionkey');
      if (this.duplicateKeys.includes(question.key)) return this.msg('duplicatedkey');
      return this.msg('questionlabel');
    },
    addStep() {
      this.localSteps.push({
        id: 'new_' + Date.now().toString(36),
        title: '',
        questions: [],
      });
      this.emit();
      this.select('step', this.localSteps[this.localSteps.length - 1].id, this.localSteps.length - 1);
    },
    /**
     * "Your details" (GUEST_DETAILS) is the step the whole check-in depends on
     * — email and invoice data. It can be moved, never removed.
     */
    isProtectedQuestion(question) {
      return question && question.type === 'GUEST_DETAILS';
    },
    isProtectedStep(step) {
      return (step.questions || []).some(this.isProtectedQuestion);
    },
    removeStep(index) {
      if (this.isProtectedStep(this.localSteps[index] || {})) return;
      this.localSteps.splice(index, 1);
      this.emit();
      this.$emit('select', null);
    },
    addQuestion(stepIndex) {
      const step = this.localSteps[stepIndex];
      const questions = [...(step.questions || [])];
      const id = 'new_' + Date.now().toString(36);
      questions.push({
        id,
        key: 'q_' + (this.totalQuestions() + 1),
        type: 'SHORT_TEXT',
        label: '',
        size: 12,
        required: false,
      });
      this.$set(this.localSteps, stepIndex, {...step, questions});
      this.emit();
      this.select('question', id, stepIndex);
    },
    removeQuestion(stepIndex, question) {
      if (this.isProtectedQuestion(question)) return;
      const step = this.localSteps[stepIndex];
      const questions = (step.questions || []).filter(item => item.id !== question.id);
      this.$set(this.localSteps, stepIndex, {...step, questions});
      this.emit();
      if (this.isActive('question', question.id)) this.$emit('select', null);
    },
    duplicateQuestion(stepIndex, question) {
      const step = this.localSteps[stepIndex];
      const questions = [...(step.questions || [])];
      const index = questions.findIndex(item => item.id === question.id);
      const id = 'new_' + Date.now().toString(36);
      const copy = {
        ...JSON.parse(JSON.stringify(question)),
        id,
        key: question.key + '_copy',
        // fresh option ids so the copy is independent
        options: (question.options || []).map((option, position) => ({
          ...option,
          id: id + '_o' + position,
        })),
      };
      questions.splice(index + 1, 0, copy);
      this.$set(this.localSteps, stepIndex, {...step, questions});
      this.emit();
      this.select('question', id, stepIndex);
    },
    setQuestions(stepIndex, questions) {
      this.$set(this.localSteps, stepIndex, {...this.localSteps[stepIndex], questions});
      this.emit();
    },
    totalQuestions() {
      return this.localSteps.reduce((sum, step) => sum + (step.questions || []).length, 0);
    },
    emit() {
      this.$emit('input', this.localSteps);
    },
  }
}
</script>

<style lang="scss" scoped>
.jsr-step {
  margin-bottom: 10px;

  &__head {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 7px 9px;
    border-radius: 9px;
    background: rgba(115, 103, 240, .08);
    cursor: pointer;
    font-weight: 600;
    color: #5e5873;

    &.is-active {
      background: #7367f0;
      color: #fff;
    }
  }

  &__drag {
    cursor: grab;
    opacity: .6;
  }

  &__index {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 18px;
    height: 18px;
    border-radius: 50%;
    background: rgba(255, 255, 255, .35);
    font-size: 10.5px;
  }

  &__title {
    flex: 1;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    font-size: 13px;
  }

  &__cond {
    color: #ff9f43;
  }

  &__del {
    opacity: .55;
  }
}

.jsr-q {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 5px 8px 5px 20px;
  border-radius: 8px;
  cursor: pointer;
  color: #6e6b7b;
  font-size: 12.5px;

  &:hover {
    background: rgba(34, 41, 47, .04);
  }

  &__actions {
    display: none;
    align-items: center;
    margin-left: auto;
  }

  &:hover &__actions,
  &.is-active &__actions {
    display: flex;
  }

  &__btn {
    padding: 2px 4px !important;
  }

  &.is-active {
    background: rgba(115, 103, 240, .14);
    color: #7367f0;
    font-weight: 600;
  }

  &__drag {
    cursor: grab;
    opacity: .45;
  }

  &__label {
    flex: 1;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__cond {
    color: #ff9f43;
  }

  &__dot {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: #ea5455;
  }
}

.jsr-add {
  display: flex;
  align-items: center;
  gap: 4px;
  margin-left: 20px;
  padding: 3px 6px;
  border: none;
  background: none;
  color: #7367f0;
  font-size: 12px;
  cursor: pointer;
}

.dark-layout {
  .jsr-step__head {
    color: #d0d2d6;
  }

  .jsr-q:hover {
    background: rgba(255, 255, 255, .05);
  }
}
.jsr-step__lock,
.jsr-q__lock {
  color: #b9b9c3;
  margin: 0 6px;
  flex-shrink: 0;
}
</style>
